import React, { useEffect, useMemo, useState } from 'react';
import '../../styled/pages/dashboard.scss';
import { Header, Loader, Success, Failed, ConfirmationModal } from '../../components';
import { customStyles } from '../../Utils/utils';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  Collapse,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Skeleton,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { getDropdownSettingsApi } from '../../rtk-apis/clinics/getDropdownSettings';
import { createDropdownSettingApi } from '../../rtk-apis/clinics/createDropdownSetting';
import { updateDropdownSettingApi } from '../../rtk-apis/clinics/updateDropdownSetting';
import { deleteDropdownSettingApi } from '../../rtk-apis/clinics/deleteDropdownSetting';
import { getReportTemplatesApi } from '../../rtk-apis/reports/getReportTemplates';
import { clearState as clearCreateState } from '../../store/slices/clinics/createDropdownSetting';
import { clearState as clearUpdateState } from '../../store/slices/clinics/updateDropdownSetting';
import { clearState as clearDeleteState } from '../../store/slices/clinics/deleteDropdownSetting';
import { labelForDropdownKey } from './dropdownKeyLabels';

const TEAL = '#2dd4bf';

interface DropdownEntry {
  _id: string;
  key: string;
  options: string[];
}

/**
 * A dropdown slot as the admin sees it: the key is FIXED — it comes from a
 * `span[data-editable]` inside the clinic's report templates, never from user
 * input. Only the option values are editable per clinic.
 */
interface SlotRow {
  key: string;
  templateNames: string[]; // which clinic templates contain this slot
  saved: DropdownEntry | null; // the clinic's saved values, if any
  orphaned: boolean; // saved values whose key no longer appears in any template
}

/**
 * Template slots that are NOT real dropdowns — free-text placeholders that
 * happen to carry data-editable spans in the seed templates. Hidden here.
 */
const EXCLUDED_KEYS = new Set(['recommendations']);

/** Extract data-editable span classes (= dropdown keys) from template HTML. */
const extractKeysFromTemplateHTML = (templateHTML: unknown): string[] => {
  if (typeof templateHTML !== 'string' || !templateHTML.trim()) {
    return [];
  }
  try {
    const doc = new DOMParser().parseFromString(templateHTML, 'text/html');
    const keys: string[] = [];
    doc.querySelectorAll('span[data-editable]').forEach(span => {
      const key = span.classList[0];
      if (key) {
        keys.push(key);
      }
    });
    return keys;
  } catch (_) {
    return [];
  }
};

const isStressTemplate = (name: string) => /stress/i.test(name);

/**
 * Which tab(s) a slot belongs to. Read from the templates the slot was found
 * in rather than from the key name — the `stress-` prefix is only a naming
 * convention, the template is the fact. Module scope so it is referentially
 * stable across renders.
 */
const slotScope = (slot: SlotRow) => {
  if (slot.templateNames.length === 0) {
    // Orphaned — no template left to read. Fall back to the key convention.
    return slot.key.startsWith('stress-')
      ? { echo: false, stress: true }
      : { echo: true, stress: false };
  }
  return {
    echo: slot.templateNames.some(n => !isStressTemplate(n)),
    stress: slot.templateNames.some(isStressTemplate),
  };
};

/** Edit-values dialog body: a controlled list of option rows. */
const OptionsEditor = ({
  options,
  onChange,
}: {
  options: string[];
  onChange: (next: string[]) => void;
}) => {
  const handleOptionChange = (index: number, value: string) => {
    const next = [...options];
    next[index] = value;
    onChange(next);
  };

  const handleDelete = (index: number) => {
    onChange(options.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    onChange([...options, '']);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
      <Typography sx={{ fontSize: 12.5, color: '#9fb2cc', fontWeight: 600 }}>Options</Typography>
      {options.map((option, index) => (
        <Box
          key={index}
          sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
        >
          <TextField
            value={option}
            onChange={e => handleOptionChange(index, e.target.value)}
            placeholder={`Option ${index + 1}`}
            multiline
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': { backgroundColor: 'rgba(255,255,255,.03)' },
            }}
          />
          <IconButton
            size="small"
            onClick={() => handleDelete(index)}
            sx={{ color: '#f87171', mt: 0.5 }}
            aria-label={`Delete option ${index + 1}`}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button
        onClick={handleAdd}
        startIcon={<AddIcon fontSize="small" />}
        sx={{
          alignSelf: 'flex-start',
          textTransform: 'none',
          color: TEAL,
          fontSize: 13,
        }}
      >
        Add option
      </Button>
    </Box>
  );
};

const DropdownSettingsPage = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { result, loading, status } = useSelector(
    (state: RootState) => state.getDropdownSettingsResponse
  );
  const { result: templatesResult, loading: templatesLoading } = useSelector(
    (state: RootState) => state.getReportTemplatesResponse
  );
  const {
    status: createStatus,
    msg: createMsg,
    loading: createLoading,
  } = useSelector((state: RootState) => state.createDropdownSettingResponse);
  const {
    status: updateStatus,
    msg: updateMsg,
    loading: updateLoading,
  } = useSelector((state: RootState) => state.updateDropdownSettingResponse);
  const {
    status: deleteStatus,
    msg: deleteMsg,
    loading: deleteLoading,
  } = useSelector((state: RootState) => state.deleteDropdownSettingResponse);

  const savedDropdowns: DropdownEntry[] = result?.result?.dropdowns || [];
  const templates: Array<{ templateName?: string; templateHTML?: unknown }> =
    templatesResult?.data || [];

  const [tab, setTab] = useState<'echo' | 'stress'>('echo');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<SlotRow | null>(null);
  const [formOptions, setFormOptions] = useState<string[]>(['']);

  const [clearTarget, setClearTarget] = useState<SlotRow | null>(null);

  const [displayMessage, setDisplayMessage] = useState('');
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isFailedModalOpen, setIsFailedModalOpen] = useState(false);

  useEffect(() => {
    dispatch(getDropdownSettingsApi());
    dispatch(getReportTemplatesApi({ search: '' }));
  }, []);

  /**
   * The fixed key list = union of data-editable slots across the clinic's
   * templates, joined with any saved per-clinic values. Saved values whose
   * key no longer appears in any template are kept visible as "orphaned".
   */
  const slots: SlotRow[] = useMemo(() => {
    const keyToTemplates = new Map<string, string[]>();
    templates.forEach(t => {
      const name = t?.templateName || 'Untitled template';
      const unique = new Set(extractKeysFromTemplateHTML(t?.templateHTML));
      unique.forEach(key => {
        if (EXCLUDED_KEYS.has(key)) {
          return;
        }
        const list = keyToTemplates.get(key) || [];
        list.push(name);
        keyToTemplates.set(key, list);
      });
    });
    const savedByKey = new Map(savedDropdowns.map(d => [d.key, d]));

    const rows: SlotRow[] = Array.from(keyToTemplates.entries())
      .sort(([a], [b]) => labelForDropdownKey(a).localeCompare(labelForDropdownKey(b)))
      .map(([key, templateNames]) => ({
        key,
        templateNames,
        saved: savedByKey.get(key) || null,
        orphaned: false,
      }));

    savedDropdowns
      .filter(d => !keyToTemplates.has(d.key) && !EXCLUDED_KEYS.has(d.key))
      .sort((a, b) => a.key.localeCompare(b.key))
      .forEach(d => rows.push({ key: d.key, templateNames: [], saved: d, orphaned: true }));

    return rows;
  }, [templates, savedDropdowns]);

  /**
   * Echo and stress slots sat in one alphabetical list, so an admin could not
   * tell which report a row belonged to (client feedback 18 Aug). Split by the
   * template the slot was found in rather than by key name — the key prefix is
   * only a convention, the template is the fact.
   *
   * A slot found in BOTH reports appears under both tabs on purpose: there is
   * one saved option list per key, shared by both templates, and hiding that
   * from one tab would make an edit look like it had no effect on the other.
   */
  const echoSlots = useMemo(() => slots.filter(s => slotScope(s).echo), [slots]);
  const stressSlots = useMemo(() => slots.filter(s => slotScope(s).stress), [slots]);
  const visibleSlots = tab === 'stress' ? stressSlots : echoSlots;
  const sharedKeys = useMemo(
    () => new Set(slots.filter(s => slotScope(s).echo && slotScope(s).stress).map(s => s.key)),
    [slots]
  );

  const showSuccess = (message: string) => {
    setDisplayMessage(message || 'Saved successfully.');
    setIsSuccessModalOpen(true);
  };
  const showFailure = (message: string) => {
    setDisplayMessage(message || 'Something went wrong.');
    setIsFailedModalOpen(true);
  };

  useEffect(() => {
    if (createStatus === 'SUCCESS') {
      showSuccess(createMsg || 'Dropdown values saved.');
      dispatch(getDropdownSettingsApi());
      setDialogOpen(false);
      dispatch(clearCreateState());
    } else if (createStatus === 'ERROR') {
      showFailure(createMsg);
      dispatch(clearCreateState());
    }
  }, [createStatus]);

  useEffect(() => {
    if (updateStatus === 'SUCCESS') {
      showSuccess(updateMsg || 'Dropdown values updated.');
      dispatch(getDropdownSettingsApi());
      setDialogOpen(false);
      dispatch(clearUpdateState());
    } else if (updateStatus === 'ERROR') {
      showFailure(updateMsg);
      dispatch(clearUpdateState());
    }
  }, [updateStatus]);

  useEffect(() => {
    if (deleteStatus === 'SUCCESS') {
      showSuccess(deleteMsg || 'Dropdown values cleared.');
      dispatch(getDropdownSettingsApi());
      setClearTarget(null);
      dispatch(clearDeleteState());
    } else if (deleteStatus === 'ERROR') {
      showFailure(deleteMsg);
      setClearTarget(null);
      dispatch(clearDeleteState());
    }
  }, [deleteStatus]);

  const openEditDialog = (slot: SlotRow) => {
    setEditingSlot(slot);
    setFormOptions(slot.saved?.options?.length ? [...slot.saved.options] : ['']);
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!editingSlot) {
      return;
    }
    const cleanedOptions = formOptions.map(o => o.trim()).filter(Boolean);
    if (editingSlot.saved) {
      dispatch(
        updateDropdownSettingApi({
          id: editingSlot.saved._id,
          key: editingSlot.key,
          options: cleanedOptions,
        })
      );
    } else {
      dispatch(
        createDropdownSettingApi({
          key: editingSlot.key,
          options: cleanedOptions,
        })
      );
    }
  };

  const isSaving = createLoading || updateLoading;
  const isLoadingAny = loading || templatesLoading;

  return (
    <section
      className="layoutContainer"
      style={customStyles.layoutBackground}
    >
      <div className="layoutGrid">
        <div className="page">
          <Header />
          <div className="page__body">
            <Box
              sx={{
                fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                maxWidth: 1040,
                mx: 'auto',
                width: '100%',
                px: { xs: 2, md: 3 },
                py: 3,
                color: '#e7eef7',
              }}
            >
              {/* heading */}
              <Box sx={{ mb: 3.5 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: TEAL,
                    fontWeight: 700,
                  }}
                >
                  Reporting
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Spectral',serif",
                    fontSize: 28,
                    fontWeight: 600,
                    color: '#e9f0f8',
                    lineHeight: 1.1,
                  }}
                >
                  Report Dropdowns
                </Typography>
                <Typography sx={{ fontSize: 13, color: '#6a7e9c', mt: 0.5, maxWidth: 620 }}>
                  Each row is a dropdown slot found in this clinic&apos;s report templates — the
                  highlighted phrases clinicians click while writing a report. The slots themselves
                  are fixed by the templates; here you manage the list of phrase options each one
                  offers.
                </Typography>
              </Box>

              {/* echo / stress split */}
              <Tabs
                value={tab}
                onChange={(_, next) => {
                  setTab(next);
                  setExpandedKey(null);
                }}
                sx={{
                  mb: 2.5,
                  minHeight: 0,
                  borderBottom: '1px solid #243652',
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#6a7e9c',
                    minHeight: 0,
                    py: 1.25,
                  },
                  '& .Mui-selected': { color: `${TEAL} !important` },
                  '& .MuiTabs-indicator': { backgroundColor: TEAL },
                }}
              >
                <Tab
                  value="echo"
                  label={`Echo report (${echoSlots.length})`}
                />
                <Tab
                  value="stress"
                  label={`Stress echo report (${stressSlots.length})`}
                />
              </Tabs>

              {/* states */}
              {isLoadingAny && !slots.length && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[0, 1, 2].map(i => (
                    <Skeleton
                      key={i}
                      variant="rounded"
                      height={72}
                      sx={{ borderRadius: '14px', bgcolor: 'rgba(255,255,255,.06)', flexShrink: 0 }}
                    />
                  ))}
                </Box>
              )}

              {!isLoadingAny && status === 'ERROR' && !slots.length && (
                <Box sx={{ textAlign: 'center', color: '#6a7e9c', py: 8 }}>
                  <Typography sx={{ color: '#f87171', fontSize: 15 }}>
                    Failed to load dropdowns.
                  </Typography>
                </Box>
              )}

              {!isLoadingAny && visibleSlots.length === 0 && status !== 'ERROR' && (
                <Card
                  elevation={0}
                  sx={{
                    flexShrink: 0,
                    background: 'linear-gradient(180deg,#102338 0%,#0c1a2b 100%)',
                    border: '1px solid #243652',
                    borderRadius: '18px',
                    textAlign: 'center',
                    py: 6,
                  }}
                >
                  <Typography sx={{ color: '#9fb2cc', fontSize: 15 }}>
                    No dropdown slots found in this clinic&apos;s
                    {tab === 'stress' ? ' stress echo ' : ' echo '}
                    report template.
                  </Typography>
                </Card>
              )}

              {/* slot list */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {visibleSlots.map(slot => {
                  const isExpanded = expandedKey === slot.key;
                  const options = slot.saved?.options || [];
                  return (
                    <Card
                      key={slot.key}
                      elevation={0}
                      sx={{
                        flexShrink: 0,
                        background: 'linear-gradient(180deg,#102338 0%,#0c1a2b 100%)',
                        border: '1px solid #243652',
                        borderRadius: '16px',
                        overflow: 'hidden',
                      }}
                    >
                      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => setExpandedKey(isExpanded ? null : slot.key)}
                            sx={{ color: '#9fb2cc' }}
                            aria-label={isExpanded ? 'Collapse options' : 'Expand options'}
                          >
                            {isExpanded ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                          </IconButton>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              sx={{
                                fontFamily: "'Spectral',serif",
                                fontSize: 17,
                                fontWeight: 600,
                                color: '#e9f0f8',
                              }}
                            >
                              {labelForDropdownKey(slot.key)}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: '#6a7e9c' }}>
                              <code style={{ fontSize: '0.95em' }}>{slot.key}</code>
                              {slot.templateNames.length > 0 &&
                                ` · In: ${slot.templateNames.join(', ')}`}
                            </Typography>
                          </Box>
                          {slot.orphaned && (
                            <Tooltip title="Saved values whose slot no longer appears in any template">
                              <Chip
                                icon={<WarningAmberIcon sx={{ fontSize: 15 }} />}
                                label="Not in any template"
                                size="small"
                                sx={{
                                  color: '#fbbf24',
                                  backgroundColor: 'rgba(251,191,36,.12)',
                                  border: '1px solid rgba(251,191,36,.4)',
                                  fontWeight: 600,
                                }}
                              />
                            </Tooltip>
                          )}
                          {sharedKeys.has(slot.key) && (
                            <Tooltip title="This slot is used by both the echo and the stress echo report. There is one shared list of values — editing it here changes it in both.">
                              <Chip
                                label="Shared with both reports"
                                size="small"
                                sx={{
                                  color: '#9fb2cc',
                                  backgroundColor: 'rgba(159,178,204,.10)',
                                  border: '1px solid rgba(159,178,204,.35)',
                                  fontWeight: 600,
                                }}
                              />
                            </Tooltip>
                          )}
                          <Chip
                            label={
                              slot.saved
                                ? `${options.length} option${options.length === 1 ? '' : 's'}`
                                : 'No values yet'
                            }
                            size="small"
                            sx={{
                              color: slot.saved ? TEAL : '#6a7e9c',
                              backgroundColor: slot.saved
                                ? 'rgba(45,212,191,.13)'
                                : 'rgba(255,255,255,.05)',
                              border: slot.saved ? `1px solid ${TEAL}55` : '1px solid #243652',
                              fontWeight: 600,
                            }}
                          />
                          <Tooltip title="Edit values">
                            <IconButton
                              size="small"
                              onClick={() => openEditDialog(slot)}
                              sx={{ color: '#9fb2cc' }}
                              aria-label={`Edit values for ${slot.key}`}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {slot.saved && (
                            <Tooltip title="Clear values">
                              <IconButton
                                size="small"
                                onClick={() => setClearTarget(slot)}
                                sx={{ color: '#f87171' }}
                                aria-label={`Clear values for ${slot.key}`}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Collapse in={isExpanded}>
                          <Box
                            sx={{
                              mt: 1.5,
                              pt: 1.5,
                              borderTop: '1px solid #243652',
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 0.75,
                            }}
                          >
                            {options.map((option, i) => (
                              <Chip
                                key={i}
                                label={option}
                                size="small"
                                sx={{
                                  color: '#c7d4e6',
                                  backgroundColor: 'rgba(255,255,255,.05)',
                                  border: '1px solid #243652',
                                }}
                              />
                            ))}
                            {options.length === 0 && (
                              <Typography sx={{ fontSize: 12.5, color: '#6a7e9c' }}>
                                No values configured — this dropdown is empty in the editor.
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            </Box>
          </div>
        </div>
      </div>

      {/* Edit-values dialog (key is fixed, never editable) */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <span>
            Edit values — {editingSlot ? labelForDropdownKey(editingSlot.key) : ''}{' '}
            <code style={{ fontSize: '0.7em', color: '#6a7e9c' }}>({editingSlot?.key})</code>
          </span>
          <IconButton onClick={() => setDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <OptionsEditor
            options={formOptions}
            onChange={setFormOptions}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{ textTransform: 'none', color: '#9fb2cc' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disableElevation
            disabled={isSaving}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: '#04221d',
              background: 'linear-gradient(90deg,#14b8a6,#2dd4bf)',
              '&:hover': { background: 'linear-gradient(90deg,#0f9e8e,#26c2ad)' },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        isConfirmationModalOpen={Boolean(clearTarget)}
        title="Clear dropdown values"
        description={`Remove all saved values for "${clearTarget?.key}"? The dropdown will show no options in the report editor until values are added again.`}
        cancelActionText="Cancel"
        confirmActionText="Clear"
        handleCloseModal={() => setClearTarget(null)}
        handleConfirm={() => {
          if (clearTarget?.saved) {
            dispatch(deleteDropdownSettingApi({ id: clearTarget.saved._id }));
          }
        }}
      />

      <Loader isLoading={isLoadingAny || isSaving || deleteLoading} />
      <Success
        handleCloseModal={() => setIsSuccessModalOpen(false)}
        title={displayMessage}
        isSuccessModalOpen={isSuccessModalOpen}
      />
      <Failed
        handleCloseModal={() => setIsFailedModalOpen(false)}
        title={displayMessage}
        isFailedModalOpen={isFailedModalOpen}
      />
    </section>
  );
};

export default DropdownSettingsPage;
